import { ArrowRight, Check } from "lucide-react";
import { PACKAGES } from "@/lib/site-data";
import FadeIn from "@/components/FadeIn";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import InstantQuote from "@/components/InstantQuote";

function PackageCard({ pkg, index }) {
  const isFeatured = index === 0; // Maldives

  return (
    <FadeIn
      delay={index * 90}
      className={`group relative flex flex-col rounded-3xl overflow-hidden bg-white card-shadow border border-black/5 hover:-translate-y-1 transition-transform duration-500 ${
        isFeatured ? "lg:col-span-2 lg:row-span-2" : ""
      }`}
    >
      <div
        className={`relative overflow-hidden ${
          isFeatured ? "aspect-[16/10]" : "aspect-[4/3]"
        }`}
      >
        <img
          src={pkg.image}
          alt={pkg.country}
          className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <Badge
            data-testid={`pkg-badge-${pkg.id}`}
            className="bg-white/90 text-[#0F3D3E] hover:bg-white border-0 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-medium"
          >
            {pkg.badge}
          </Badge>
        </div>
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between gap-4 text-white">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/80">
              {pkg.flag} {pkg.country}
            </div>
            <div
              className={`font-display leading-none mt-1 ${
                isFeatured ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"
              }`}
            >
              {pkg.duration}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/75">
              From
            </div>
            <div className="font-display text-2xl sm:text-3xl leading-none">
              {pkg.price}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-6 sm:p-7">
        <p
          className={`font-display text-[#0F3D3E] ${
            isFeatured ? "text-2xl sm:text-3xl" : "text-xl"
          } leading-snug`}
        >
          {pkg.tagline}
        </p>
        <p className="mt-3 text-[#0A0A0A]/70 text-sm leading-relaxed">
          {pkg.description}
        </p>

        <ul
          className={`mt-5 grid gap-2 ${
            isFeatured ? "sm:grid-cols-2" : "grid-cols-1"
          }`}
        >
          {pkg.inclusions.map((inc) => (
            <li
              key={inc}
              className="flex items-start gap-2 text-sm text-[#0A0A0A]/80"
            >
              <Check size={16} className="mt-0.5 text-[#2C7A7B] shrink-0" />
              <span>{inc}</span>
            </li>
          ))}
        </ul>

        {pkg.resorts.length > 0 && (
          <div className="mt-5 rounded-2xl bg-[#F4EDE4] p-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#0F3D3E] mb-2">
              Featured Resorts
            </div>
            <div className="flex flex-wrap gap-1.5">
              {pkg.resorts.slice(0, 6).map((r) => (
                <span
                  key={r}
                  className="text-xs bg-white text-[#0F3D3E] rounded-full px-3 py-1 border border-[#0F3D3E]/10"
                >
                  {r}
                </span>
              ))}
              {pkg.resorts.length > 6 && (
                <span className="text-xs text-[#2C7A7B] font-medium self-center">
                  +{pkg.resorts.length - 6} more
                </span>
              )}
            </div>
          </div>
        )}

        <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid={`pkg-details-${pkg.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-[#0F3D3E] text-[#F4EDE4] px-5 py-2.5 text-sm font-medium hover:bg-[#2C7A7B] transition-colors"
              >
                View details
                <ArrowRight size={16} />
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid={`pkg-dialog-${pkg.id}`}
              className="max-w-2xl bg-[#FAFAFA] border-[#0F3D3E]/10"
            >
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-[#0F3D3E]">
                  {pkg.country} · {pkg.duration}
                </DialogTitle>
                <DialogDescription className="text-[#4B5563]">
                  Starting from <strong>{pkg.price}</strong> — {pkg.priceNote}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-[#0A0A0A]/80 leading-relaxed">
                  {pkg.description}
                </p>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#2C7A7B] mb-2">
                    Inclusions
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {pkg.inclusions.map((inc) => (
                      <li
                        key={inc}
                        className="flex items-start gap-2 text-sm text-[#0A0A0A]/80"
                      >
                        <Check size={16} className="mt-0.5 text-[#2C7A7B]" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
                {pkg.resorts.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#2C7A7B] mb-2">
                      Resorts we love
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pkg.resorts.map((r) => (
                        <span
                          key={r}
                          className="text-xs bg-[#F4EDE4] text-[#0F3D3E] rounded-full px-3 py-1.5"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <a
                  href="#contact"
                  data-testid={`pkg-dialog-inquire-${pkg.id}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0F3D3E] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#2C7A7B]"
                >
                  Send an inquiry <ArrowRight size={16} />
                </a>
                <InstantQuote pkg={pkg} variant="card" />
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <InstantQuote pkg={pkg} variant="ghost" />
        </div>
      </div>
    </FadeIn>
  );
}

export default function Packages() {
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative py-24 sm:py-32 bg-[#F4EDE4]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 lg:mb-20">
          <div className="max-w-2xl">
            <FadeIn>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#2C7A7B]">
                <span className="h-px w-8 bg-[#2C7A7B]" />
                Featured Packages
              </div>
            </FadeIn>
            <FadeIn delay={100}>
              <h2 className="mt-5 font-display text-[#0F3D3E] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Journeys we{" "}
                <span className="italic">love</span> planning.
              </h2>
            </FadeIn>
          </div>
          <FadeIn delay={200}>
            <p className="max-w-md text-[#0A0A0A]/70 leading-relaxed">
              A selection of our most-requested escapes — fully customisable,
              always backed by trusted DMCs, and priced honestly.
            </p>
          </FadeIn>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7 auto-rows-fr">
          {PACKAGES.map((pkg, i) => (
            <PackageCard key={pkg.id} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
